import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { NgxSpinnerService } from "ngx-spinner";
import { environment } from "../../../environments/environment";
import { InspectionService } from "../shared/services/inspection.service";
import { Inspection, InspectionStatuses, InspectionTypes as ITPS} from "./../shared/models/Inspection";
import * as Constants from "../../models/Constants";
import { ErisFormComponent } from "eris-forms";
import { AlertService } from "../../shared/services/alert.service";
import { ErrorHandlerService } from "../../services/error-handler.service";
import { InspectionDraftService, InspectionDraftModel } from "../shared/services/inspection-draft.service";
import { ErisLoginService } from "../../services/sso/eris-login.service";
import { HttpParams } from "@angular/common/http";

@Component({
  selector: "app-create-local-gmp-inspection",
  templateUrl: "./create-local-gmp-inspection.component.html",
  styleUrls: ["./create-local-gmp-inspection.component.css"],
})
export class CreateLocalGmpInspectionComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(ErisFormComponent) erisFormComponent!: ErisFormComponent;
  public currentInspectionType: string;
  public currentInspectionName: string;
  public productionSubTypeModule: any;
  public previousInspectionId: any;
  public approvalMethod: string = Constants.CATEGORY_B;
  public options: FormlyFormOptions = {};
  public form = new FormGroup({});
  public isEditMode = false;
  public inspection: Inspection;
  public categoryId : number = 0;
  public agencyId : number = 0;
  public applicantTypeId : number = 0;
  public isFIREditing : boolean = false;
  public draft: InspectionDraftModel;
  public draftId: number;
  private isSavingDraft: boolean = false;

  constructor(private inspectionService: InspectionService, private spinner: NgxSpinnerService, private router: Router,
    private activatedRoute: ActivatedRoute, private cdr: ChangeDetectorRef, private alertService: AlertService,
    private errorHandlerService: ErrorHandlerService, private inspectionDraftService: InspectionDraftService,
    private erisLoginService: ErisLoginService) {
    this.currentInspectionType = this.activatedRoute.snapshot.params[
      "inspectionTypeCode"
    ];

    this.activatedRoute.params.subscribe((params) => {
      const inspectionTypeCode = params["inspectionTypeCode"];
      this.currentInspectionName = ITPS.codeToName(inspectionTypeCode);
      this.currentInspectionType = inspectionTypeCode;
    });
  }

  private mergeDraftModels(...sources: any[]): any {
    const target: any = {};
    for (const src of sources) {
      if (!src || typeof src !== 'object') continue;
      for (const key of Object.keys(src)) {
        const val = src[key];
        if (val && typeof val === 'object' && !Array.isArray(val) && !(val instanceof Date)) {
          target[key] = this.mergeDraftModels(target[key] || {}, val);
        } else if (val !== undefined && val !== null && val !== '') {
          target[key] = val;
        } else if (!(key in target)) {
          target[key] = val;
        }
      }
    }
    return target;
  }

  private saveDraftListener = (e: any) => {
    const m = e?.detail?.model || {};
    if (typeof e?.detail?.activeStep === 'number') {
      m.activeStep = e.detail.activeStep;
    }
    this.saveDraft(m);
  };

  ngOnInit(): void {
    window.addEventListener('eris-save-draft', this.saveDraftListener);
    if (this.fiels?.[0]) {
      (this.fiels[0] as any).templateOptions = {
        ...((this.fiels[0] as any).templateOptions || {}),
        saveDraft: (m?: any) => this.saveDraft(m)
      };
    }
    if (this.fields?.[0]) {
      (this.fields[0] as any).templateOptions = {
        ...((this.fields[0] as any).templateOptions || {}),
        saveDraft: (m?: any) => this.saveDraft(m)
      };
    }
    this.options.formState = {
      ...this.options.formState,
      saveDraft: (m?: any) => this.saveDraft(m)
    };
    this.fetchCandidateFacilities();
  }

  ngOnDestroy(): void {
    window.removeEventListener('eris-save-draft', this.saveDraftListener);
  }

  ngAfterViewInit() : void{
    const id = this.activatedRoute.snapshot.params['id'];
    const draftId = this.activatedRoute.snapshot.queryParams['draftId'] || this.activatedRoute.snapshot.params['draftId'];
    if(id){
      this.isEditMode = true;
      this.fetchInspectionData(id);
    } else if (draftId) {
      this.draftId = +draftId;
      this.fetchDraftData(this.draftId);
    }
  }

  fetchDraftData(draftId: number) {
    this.spinner.show();
    this.inspectionDraftService.getDraft(draftId).subscribe(
      draft => {
        this.spinner.hide();
        if (draft && draft.inspectionJsonData) {
          this.draft = draft;
          const sId = draft.id || (draft as any)?.ID;
          if (sId) {
            this.draft.id = sId;
            this.draftId = sId;
          }
          try {
            const parsed = JSON.parse(draft.inspectionJsonData);
            if (parsed.productionInformation) {
              const prodDetails = parsed.productionInformation.productDetailData?.productDetails || parsed.productionInformation.productDetails;
              if (prodDetails) {
                parsed.productionInformation.productDetails = prodDetails;
                parsed.productionInformation.productDetailData = { productDetails: prodDetails };
              }
            }
            if (typeof parsed.activeStep === 'number') {
              if (this.options?.formState) {
                (this.options.formState as any).activeStep = parsed.activeStep;
              }
            }
            if (this.erisFormComponent) {
              this.erisFormComponent.model = parsed;
            }
            this.cdr.detectChanges();
          } catch (e) {
            console.error('Error parsing draft data', e);
          }
        }
      },
      err => {
        this.spinner.hide();
        this.alertService.error('Unable to fetch drafted inspection');
      }
    );
  }

  saveDraft(model?: any) {
    if (this.isSavingDraft) {
      return;
    }
    this.isSavingDraft = true;
    this.spinner.show();
    const formVal = this.erisFormComponent?.form?.value || {};
    const rootModel = this.erisFormComponent?.model || {};
    const passedModel = model || {};
    const currentModel = this.mergeDraftModels(rootModel, passedModel, formVal);

    const prodDetails =
      passedModel.productionInformation?.productDetailData?.productDetails ||
      passedModel.productionInformation?.productDetails ||
      rootModel.productionInformation?.productDetailData?.productDetails ||
      rootModel.productionInformation?.productDetails ||
      formVal.productionInformation?.productDetailData?.productDetails ||
      formVal.productionInformation?.productDetails ||
      currentModel.productionInformation?.productDetailData?.productDetails ||
      currentModel.productionInformation?.productDetails;

    if (prodDetails && Array.isArray(prodDetails) && prodDetails.length > 0) {
      if (!currentModel.productionInformation) {
        currentModel.productionInformation = {};
      }
      currentModel.productionInformation.productDetails = prodDetails;
      currentModel.productionInformation.productDetailData = { productDetails: prodDetails };
    }

    const step = (model && typeof model.activeStep === 'number')
      ? model.activeStep
      : (typeof rootModel.activeStep === 'number'
          ? rootModel.activeStep
          : (typeof (this.options?.formState as any)?.activeStep === 'number'
              ? (this.options.formState as any).activeStep
              : undefined));
    if (typeof step === 'number') {
      currentModel.activeStep = step;
      if (this.options?.formState) {
        (this.options.formState as any).activeStep = step;
      }
    }

    if (this.erisFormComponent) {
      this.erisFormComponent.model = currentModel;
    }

    const typeCode = this.currentInspectionType || 'NLGI';
    const userId = this.erisLoginService.getUserId();

    if (!this.draft) {
      this.draft = new InspectionDraftModel(JSON.stringify(currentModel), userId, 0);
      this.draft.inspectionTypeCode = typeCode;
      if (this.draftId) {
        this.draft.id = this.draftId;
      }
    } else {
      if (!this.draft.id && this.draftId) {
        this.draft.id = this.draftId;
      }
      this.draft.inspectionJsonData = JSON.stringify(currentModel);
      this.draft.inspectionTypeCode = typeCode;
      this.draft.savedBy = userId;
      this.draft.createdBy = userId;
    }

    this.inspectionDraftService.saveDraft(this.draft).subscribe(
      res => {
        this.isSavingDraft = false;
        this.spinner.hide();
        if (res) {
          const sId = res.id || (res as any)?.ID;
          if (sId) {
            res.id = sId;
            this.draftId = sId;
          }
          this.draft = res;
          this.alertService.success('Inspection application saved as a draft successfully');
        } else {
          this.alertService.error('Unable to save inspection application as a draft!');
        }
      },
      error => {
        this.isSavingDraft = false;
        this.spinner.hide();
        this.alertService.error(this.errorHandlerService.handleApiError(error));
      }
    );
  }

  public candidateFacilities: any[] = [];
  public lastFacilitySearchTerm: string = '';

  fetchCandidateFacilities(): void {
    this.inspectionService.getFacilities().subscribe(
      (res: any[]) => {
        if (res && Array.isArray(res)) {
          res.forEach((element: any) => {
            const appNum = (element.applicationID || element.applicationNumber)
              ? ` (${element.applicationID || ''}${element.applicationNumber || ''}) `
              : '';
            element.name = (element.name || '') + appNum;
            element.isLegacyData = false;
          });
          this.candidateFacilities = res;
        }
      },
      (err) => {
        console.error('Error fetching candidate facilities', err);
      }
    );
  }

  public buildLegacyInspectionParams(term?: string): HttpParams {
    this.lastFacilitySearchTerm = term || '';
    let params = new HttpParams()
      .set('draw', '1')
      .set('start', '0')
      .set('length', '250')
      .set('legacyDataFilter', 'true')
      .append('inspectionTypeCodes', ITPS.NEW_LOCAL_GMP_INSPECTION)
      .append('inspectionTypeCodes', ITPS.LOCAL_GMP_RENEWAL_INSPECTION)
      .append('inspectionTypeCodes[]', ITPS.NEW_LOCAL_GMP_INSPECTION)
      .append('inspectionTypeCodes[]', ITPS.LOCAL_GMP_RENEWAL_INSPECTION);

    if (term && term.trim()) {
      params = params
        .set('search[value]', term.trim())
        .set('search', term.trim());
    } else {
      params = params.set('search[value]', '');
    }
    return params;
  }

  public getLegacyFacilityUrl(term?: string): string {
    const params = this.buildLegacyInspectionParams(term);
    return `${environment.baseUrl}/Inspection/List?${params.toString()}`;
  }

  public getLegacyFacilityData(term?: string): HttpParams {
    return this.buildLegacyInspectionParams(term);
  }

  public formatLegacyFacilities(res: any): any[] {
    const items = res?.data || (Array.isArray(res) ? res : []);
    const legacyList = items.map((element: any) => {
      const inspNum = element.inspectionNumber ? ` (${element.inspectionNumber})` : '';
      const facilityName = element.facilityName || element.name || '';
      const name = `${facilityName}${inspNum}`.trim() || 'Legacy Facility';
      return {
        ...element,
        name,
        id: element.facilityID || element.facilityId || element.id,
        inspectionID: element.id,
        isLegacyData: true,
        inspectionStatusCode: element.inspectionStatusCode,
      };
    });

    const termLower = (this.lastFacilitySearchTerm || '').trim().toLowerCase();
    const matchingCandidates = (this.candidateFacilities || [])
      .filter((c: any) => {
        if (!termLower) return true;
        const cName = (c.name || '').toLowerCase();
        const cApp = (c.applicationNumber || '').toLowerCase();
        return cName.includes(termLower) || cApp.includes(termLower);
      })
      .map((c: any) => ({
        ...c,
        isLegacyData: false,
      }));

    return [...legacyList, ...matchingCandidates];
  }

  fiels: any = [
    {
      type: "draft-steps",
      templateOptions: {},
      fieldGroup: [
        {
          key: "facilities",
          fieldGroupClassName: "row",
          templateOptions: { title: "Facilities" },
          fieldGroup: [
            {
              fieldGroupClassName: "row",
              templateOptions: { title: "Facilities" },
              fieldGroup: [
                {
                  key: "facility",
                  type: "lookup",
                  className: "col-md-6",
                  templateOptions: {
                    label: "Facilities",
                    required: true,
                    multiple: false,
                    prefetch: false,
                    searchable: true,
                    minLengthTerm: 1,
                    disabled: false,
                    responseFn: (res) => ({
                      name: res.name,
                      id: res.id,
                      inspectionID: res.inspectionID,
                      isLegacyData: res.isLegacyData,
                      inspectionStatusCode: res.inspectionStatusCode,
                    }),
                    list: {
                      method: "post",
                      url: (term) => this.getLegacyFacilityUrl(term),
                      data: (term) => this.getLegacyFacilityData(term),
                      responseFn: (res) => this.formatLegacyFacilities(res),
                    },
                  },
                  validators: {
                    validation: ["legacy"],
                  },
                  expressionProperties: {
                    "className": () => {
                      return this.isEditMode ? "col-md-6 disabled" : "col-md-6";
                    },
                  }
                },
                {
                  type: 'edit-anchor',
                  key: 'editApplication',
                  className: "col-md-6",
                  templateOptions: {
                    inspectionType: 'NLGI'
                  },
                  hideExpression: (
                    model: any,
                    formState: any,
                    field: FormlyFieldConfig
                  ) => {
                    return model.facility?.isLegacyData && model.facility?.inspectionStatusCode != InspectionStatuses.LEGACY_DATA_EDITED ? false: true;
                  },
                },
              ],
            },
          ],
          hideExpression: (model: any, formState: any, field: FormlyFieldConfig) => {
            return this.isFIREditing;
          },
        },
        {
          key: "productionInformation",
          fieldGroupClassName: "row",
          templateOptions: { title: "Production Information" },
          fieldGroup: [
            {
              key: "productDetailData",
              type: "gmp-product-detail",
              className: "col-md-12",
              templateOptions: {
                isDisabled: false,
                editing: false
              },
              expressionProperties: {
                "templateOptions.editing": () => {
                  return this.isEditMode;
                },
              }
            },
          ],
          hideExpression: (model: any, formState: any, field: FormlyFieldConfig) => {
            return this.isFIREditing;
          },
        },
        {
          key: "fileAttachment",
          templateOptions: {
            title: "File Attachment",
            description: "",
            icon: "fa fa-info-circle",
          },
          fieldGroup: [
            {
              key: "documents",
              type: "upload-list",

              templateOptions: {
                label: "Applicant Name",
                required: true,
                items: [],
                dependsOnField: "facilities.facility",
                dataSourceFn: (submoduleCode) => {
                  return {
                    url: `/DocumentRef/GMP/${submoduleCode}`,
                  };
                },
                extraData: {
                  urlParameter: "",
                  originalFileName: "-",
                  editing: false,
                  categoryTypeID: "",
                  AgencyTypeID: "",
                  ApplicantTypeID: "",
                },
                responseFn: (res) => res,
              },
              expressionProperties: {
                "templateOptions.extraData.urlParameter": (
                  model,
                  formState,
                  field
                ) => {
                  return this.currentInspectionType;
                },
                "templateOptions.extraData.editing": () => {
                  return this.isEditMode;
                },
                "templateOptions.extraData.categoryTypeID": () => {
                  return this.categoryId;
                },
                "templateOptions.extraData.AgencyTypeID": () => {
                  return this.agencyId;
                },
                "templateOptions.extraData.ApplicantTypeID": () => {
                  return this.applicantTypeId;
                },
              },
            },
          ],
        },
      ],
    },
  ];


  fields: any = [
    {
      type: "draft-steps",
      templateOptions: {},
      fieldGroup: [
        {
          key: "facilities",
          fieldGroupClassName: "row",
          templateOptions: { title: "Facilities" },
          fieldGroup: [
            {
              fieldGroupClassName: "row",
              templateOptions: { title: "Facilities" },
              fieldGroup: [
                {
                  key: "facility",
                  type: "lookup",
                  className: "col-md-6",
                  templateOptions: {
                    label: "Facilities",
                    required: true,
                    multiple: false,
                    prefetch: true,
                    searchable: false,
                    minLengthTerm: 0,
                    responseFn: (res) => {
                      if(res.inspectionID){
                        this.previousInspectionId = res.inspectionID;
                        this.fetchInspectionData(this.previousInspectionId);
                      }            
                      return {
                        name: res.name,
                        id: res.id,
                        inspectionID: res.inspectionID,
                        isLegacyData: res.isLegacyData,
                        inspectionStatusCode: res.inspectionStatusCode,
                      }
                    },
                    list: {
                      method: "get",
                      url: (term) =>
                        `${environment.baseUrl}/Inspection/LocalGMPInspection/Renewal/GetFacilities`,
                      responseFn: (res) => {
                        res?.forEach(element => {
                          element.name = element.name + " (" + element.applicationID + element.applicationNumber + ") "
                        });
                        return res;
                      },
                    },
                  },
                  validators: {
                    validation: ["legacy"],
                  },
                  expressionProperties: {
                    "className": () => {
                      return this.isEditMode ? "col-md-6 disabled" : "col-md-6";
                    },
                  },
                },
                {
                  type: 'edit-anchor',
                  key: 'editApplication',
                  className: "col-md-6",
                  templateOptions: {
                    inspectionType: 'LGRI'
                  },
                  hideExpression: (
                    model: any,
                    formState: any,
                    field: FormlyFieldConfig
                  ) => {
                    return model.facility?.isLegacyData && model.facility?.inspectionStatusCode != InspectionStatuses.LEGACY_DATA_EDITED ? false: true;
                  },
                },
              ],
            },
          ],
          hideExpression: (model: any, formState: any, field: FormlyFieldConfig) => {
            return this.isFIREditing;
          },
        },
        {
          key: "productionInformation",
          fieldGroupClassName: "row",
          templateOptions: { title: "Production Information" },
          fieldGroup: [
            {
              key: "productDetailData",
              type: "gmp-product-detail",
              className: "col-md-12",
              templateOptions: {
                isDisabled: false,
                editing: false,
                isDataFilled: true
              },
              expressionProperties: {
                "templateOptions.editing": () => {
                  return this.isEditMode;
                },
              }
            },
          ],
          hideExpression: (model: any, formState: any, field: FormlyFieldConfig) => {
            return this.isFIREditing;
          },
        },
        {
          key: "fileAttachment",
          templateOptions: {
            title: "File Attachment",
            description: "",
            icon: "fa fa-info-circle",
          },
          fieldGroup: [
            {
              key: "documents",
              type: "upload-list",

              templateOptions: {
                label: "Applicant Name",
                required: true,
                items: [],
                dependsOnField: "facilities.facility",
                dataSourceFn: (submoduleCode) => {
                  return {
                    url: `/DocumentRef/GMP/${submoduleCode}`,
                  };
                },
                extraData: {
                  urlParameter: "",
                  originalFileName: "-",
                  editing: false,
                  categoryTypeID: "",
                  AgencyTypeID: "",
                  ApplicantTypeID: "",
                },
                responseFn: (res) => res,
              },
              expressionProperties: {
                "templateOptions.extraData.urlParameter": (
                  model,
                  formState,
                  field
                ) => {
                  return this.currentInspectionType;
                },
                "templateOptions.extraData.editing": () => {
                  return this.isEditMode;
                },
                "templateOptions.extraData.categoryTypeID": () => {
                  return this.categoryId;
                },
                "templateOptions.extraData.AgencyTypeID": () => {
                  return this.agencyId;
                },
                "templateOptions.extraData.ApplicantTypeID": () => {
                  return this.applicantTypeId;
                },
              },
            },
          ],
        },
      ],
    },
  ];

  onSubmit(event) {
    this.spinner.show();
    var link = "New";
    if (this.currentInspectionType == "LGRI") {
      event.previousInspectionId = this.previousInspectionId;
      link = "Renewal";
    }

    if(this.isFIREditing) {
      this.inspectionService.submitFurtherInformation(this.inspection.id, event.fileAttachment).subscribe(res => {
        this.spinner.hide();
        if (res) {
          this.alertService.success("Your Application was submitted successfully");
          this.router.navigate(["inspections/localGMP/list/LOCALGMP"]);
        } else {
          this.alertService.error("Unable to submit your Application, please try again!");
        }
      }, error => {
        console.log(error);
        this.spinner.hide();
        this.alertService.error(this.errorHandlerService.handleApiError(error));
      });    
    } else {
      event.productionInformation["productDetails"] = event.productionInformation.productDetailData?.productDetails;

      if(!this.isEditMode){
        this.inspectionService.postLocalGMPForm(event, link).subscribe(data => {
          this.spinner.hide();
          if (data) {
            if (this.draft && this.draft.id) {
              this.inspectionDraftService.deleteDraft(this.draft.id, this.erisLoginService.getUserId()).subscribe();
            }
            this.alertService.success("Your Application was submitted successfully");
            this.router.navigate(["inspections/localGMP/list/LOCALGMP"]);
          } else {
            this.alertService.error("Unable to submit your Application, please try again!");
          }
        }, error => {
          console.log(error);
          this.spinner.hide();
          this.alertService.error(this.errorHandlerService.handleApiError(error));
        });
      } else {
        event.inspectionId = this.inspection.id;
        event.previousInspectionId = this.currentInspectionType == "LGRI" ? this.inspection.previousInspectionID : 0;
        this.inspectionService.editLocalGMPForm(event, link).subscribe(data => {
          this.spinner.hide();
          if(data){
            if (this.draft && this.draft.id) {
              this.inspectionDraftService.deleteDraft(this.draft.id, this.erisLoginService.getUserId()).subscribe();
            }
            this.alertService.success("Your Application was edited successfully");
            this.router.navigate(["inspections/localGMP/list/LOCALGMP"]);
          } else {
            this.alertService.error("Unable to edit your Application, please try again!");
          }
        }, error => {
          console.log(error);
          this.spinner.hide();
          this.alertService.error(this.errorHandlerService.handleApiError(error));
        });
      }  
    }

  }

  fetchInspectionData(inspectionId){
    this.spinner.show();
    this.inspectionService.getInspection(inspectionId).subscribe(data => {
      this.isFIREditing =  [InspectionStatuses.FIR_REQUESTED_BEFORE_INSPECTION, 
        InspectionStatuses.FIR_REQUESTED_AFTER_INSPECTION].includes(data.status.inspectionStatusCode);

      let newProductionInformation = {
        productDetailData: {
          productDetails: data.productionInfo.productDetails
        }
      };

      if(!this.isEditMode){
        var tempModel = {
          facilities: this.erisFormComponent.form.value.facilities,
          productionInformation: newProductionInformation,
          fileAttachment: this.erisFormComponent.form.value.fileAttachment
        };

        this.erisFormComponent.model = tempModel;
      } else {
        this.inspection = data;
        this.erisFormComponent.model = {
          facilities: { facility : {id: data.facilityInfo.facilityID, name: data.facilityInfo.name} },
          productionInformation: newProductionInformation,
          fileAttachment: {documents: data.documents}
        };
      }

      this.cdr.detectChanges();
      this.spinner.hide()
    }, err => {
      this.spinner.hide();
      if(this.isEditMode){
        this.alertService.error("The Inspection Couldn't be Retrieved for Editing!");
      } else {
        this.alertService.error("The Previous Inspection Couldn't be Retrieved!");
      }
    })
  }
}
